import { ReactNode } from 'react';
import { useFormContext } from 'react-hook-form';
import classNames from '@/lib/classNames';
import { FormControl, FormField, FormItem, FormLabel } from '../../elements/Form';
import { Select, SelectContent, SelectTrigger, SelectValue } from '../../elements/Select/Select';
import { BookingSearchFormData } from './BookingSearchBar';

interface BookingSearchSelectItemProps {
    name: keyof BookingSearchFormData;
    children: ReactNode;
    hasError: boolean;
    label?: string;
    ariaLabel?: string;
    className?: string;
}

export const BookingSearchSelectItem = ({ name, label, ariaLabel, children, hasError, className }: BookingSearchSelectItemProps) => {
    const { control, formState } = useFormContext();
    const { watch, setValue } = useFormContext();

    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => (
                <FormItem className={classNames('w-full relative min-h-[50px] min-w-[100px] lg:w-1/2', className)}>
                    <FormLabel className="text-[0.5rem] 2xl:text-[0.75rem] uppercase" float={true} activateFloat={field.value}>
                        {label ?? ariaLabel}
                    </FormLabel>

                    <Select
                        onValueChange={(e) => {
                            field.onChange(e);
                            if (
                                field.name === 'pickUpLocation' &&
                                !formState.dirtyFields.dropOffLocation // if user has already set drop off location, don't change it
                            ) {
                                setTimeout(() => {
                                    setValue('dropOffLocation', watch('pickUpLocation'));
                                }, 50); // if user changes between countries value doesn't update in time, so adding timeout
                            }
                        }}
                        defaultValue={field.value}
                        value={field.value}
                    >
                        <FormControl>
                            <SelectTrigger aria-label={ariaLabel} className={classNames('leading-120 font-normal rounded-xl', hasError && 'text-destructive border border-destructive p-1')}>
                                {/* Can't style select value so have to do this here
                https://github.com/radix-ui/primitives/issues/1363#issuecomment-1168517300 */}
                                <span className="truncate">
                                    <SelectValue placeholder={ariaLabel} />
                                </span>
                            </SelectTrigger>
                        </FormControl>
                        <SelectContent className="max-h-80">{children}</SelectContent>
                    </Select>
                </FormItem>
            )}
        />
    );
};
